<template>
    <div>
        <!-- ===== MAIN ACTION MENU ===== -->
        <v-dialog v-model="dialog" max-width="260" persistent>
            <v-card class="action-card">
                <div class="action-header">
                    <div class="action-title">More</div>

                    <v-btn icon small class="close-btn" @click="close">
                        <v-icon small>mdi-close</v-icon>
                    </v-btn>
                </div>
                <v-list dense class="action-list">
                    <template v-if="hasOrders">
                        <v-list-item @click="viewOrders">
                            <v-list-item-content>
                                <v-list-item-title class="action-text">
                                    View Orders
                                </v-list-item-title>
                            </v-list-item-content>
                            <v-icon small color="#2563eb">
                                mdi-eye-circle
                            </v-icon>
                        </v-list-item>
                        <v-list-item @click="askConfirm('delete')">
                            <v-list-item-content>
                                <v-list-item-title class="action-text delete">Delete Shift</v-list-item-title>
                            </v-list-item-content>
                            <v-icon small color="#ef4444">mdi-delete-outline</v-icon>
                        </v-list-item>
                    </template>
                    <template v-else>
                        <v-list-item @click="editShift">
                            <v-list-item-content>
                                <v-list-item-title class="action-text">Edit Shift</v-list-item-title>
                            </v-list-item-content>
                            <v-icon small color="#16a34a">mdi-pencil-outline</v-icon>
                        </v-list-item>
                        <v-list-item @click="copyShift">
                            <v-list-item-content>
                                <v-list-item-title class="action-text">Copy Shift</v-list-item-title>
                            </v-list-item-content>
                            <v-icon small color="#2563eb">mdi-content-copy</v-icon>
                        </v-list-item>

                        <v-list-item v-if="!isPublished" @click="askConfirm('publish')">
                            <v-list-item-content>
                                <v-list-item-title class="action-text publish">Publish Shift</v-list-item-title>
                            </v-list-item-content>
                            <v-icon small color="#2563eb">mdi-send-outline</v-icon>
                        </v-list-item>

                        <v-list-item v-if="isPublished" @click="askConfirm('unpublish')">
                            <v-list-item-content>
                                <v-list-item-title class="action-text unpublish">Unpublish Shift</v-list-item-title>
                            </v-list-item-content>
                            <v-icon small color="#d97706">mdi-send-clock-outline</v-icon>
                        </v-list-item>

                        <v-divider class="my-1" />

                        <v-list-item @click="askConfirm('delete')">
                            <v-list-item-content>
                                <v-list-item-title class="action-text delete">Delete Shift</v-list-item-title>
                            </v-list-item-content>
                            <v-icon small color="#ef4444">mdi-delete-outline</v-icon>
                        </v-list-item>
                    </template>

                </v-list>
            </v-card>
        </v-dialog>

        <!-- ===== CONFIRM DIALOG ===== -->
        <v-dialog v-model="confirmDialog" max-width="340" persistent>
            <v-card class="confirm-card">

                <div class="confirm-icon-wrap" :class="confirmMeta.iconBg">
                    <v-icon large :color="confirmMeta.iconColor">{{ confirmMeta.icon }}</v-icon>
                </div>

                <div class="confirm-title">{{ confirmMeta.title }}</div>
                <div class="confirm-body">{{ confirmMeta.body }}</div>

                <div class="confirm-actions">
                    <v-btn outlined class="confirm-cancel-btn" @click="cancelConfirm">Cancel</v-btn>
                    <v-btn class="confirm-ok-btn" :class="confirmMeta.btnClass" @click="runConfirmed">
                        {{ confirmMeta.btnLabel }}
                    </v-btn>
                </div>

            </v-card>
        </v-dialog>
    </div>
</template>

<script>
const CONFIRM_META = {
    publish: {
        icon: 'mdi-send-outline',
        iconColor: '#2563eb',
        iconBg: 'icon-bg--blue',
        title: 'Publish Shift?',
        body: 'This shift will be visible to drivers. You can unpublish it later.',
        btnLabel: 'Yes, Publish',
        btnClass: 'btn--blue',
    },
    unpublish: {
        icon: 'mdi-send-clock-outline',
        iconColor: '#d97706',
        iconBg: 'icon-bg--amber',
        title: 'Unpublish Shift?',
        body: 'This shift will be hidden from drivers. You can re-publish it at any time.',
        btnLabel: 'Yes, Unpublish',
        btnClass: 'btn--amber',
    },
    delete: {
        icon: 'mdi-delete-outline',
        iconColor: '#ef4444',
        iconBg: 'icon-bg--red',
        title: 'Delete Shift?',
        body: 'This action cannot be undone. The shift and all its records will be permanently removed.',
        btnLabel: 'Yes, Delete',
        btnClass: 'btn--red',
    },
}

export default {
    name: "DriverShareActionModal",

    props: {
        shift: { type: Object, default: null },
    },

    data() {
        return {
            dialog: true,
            confirmDialog: false,
            pendingAction: null,
        }
    },

    computed: {
        isPublished() {
            return this.shift && Number(this.shift.status) === 20
        },
        hasOrders() {
            return this.shift &&
                Array.isArray(this.shift.orders) &&
                this.shift.orders.length > 0
        },
        confirmMeta() {
            return CONFIRM_META[this.pendingAction] || {}
        },
    },

    methods: {
        close() {
            this.dialog = false
            this.$emit('close')
        },

        editShift() {
            this.$emit('edit', this.shift)
            this.close()
        },
        copyShift() {
            this.$emit('copy', this.shift)
            this.close()
        },

        askConfirm(action) {
            this.pendingAction = action
            this.dialog = false
            this.confirmDialog = true
        },

        cancelConfirm() {
            this.confirmDialog = false
            this.pendingAction = null
            this.$emit('close')
        },

        runConfirmed() {
            const action = this.pendingAction
            this.confirmDialog = false
            this.pendingAction = null

            if (action === 'delete') {
                this.$emit('delete', this.shift)
            } else if (action === 'publish') {
                this.$emit('publish', this.shift)
            } else if (action === 'unpublish') {
                this.$emit('unpublish', this.shift)
            }

            this.$emit('close')
        },
        viewOrders() {
            this.$emit('view-orders', this.shift)
            this.close()
        },
    },
}
</script>

<style scoped>
.action-header {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    margin-bottom: 8px;
}

.close-btn {
    position: absolute;
    right: 0;
    top: -4px;
}
.action-card {
    border-radius: 14px !important;
    padding: 14px 10px;
}

.action-title {
    text-align: center;
    font-weight: 600;
    font-size: 16px;
    color: #6b7280;
    margin-bottom: 8px;
}

.action-list {
    padding: 4px;
}

.action-list .v-list-item {
    border-radius: 8px;
    min-height: 42px;
    cursor: pointer;
    transition: 0.15s ease;
}

.action-list .v-list-item:hover {
    background: #f3f4f6;
}

.action-text {
    font-size: 14px;
    font-weight: 500;
    color: #1e3a8a;
}

.action-text.publish {
    color: #2563eb;
}

.action-text.unpublish {
    color: #d97706;
}

.action-text.delete {
    color: #ef4444;
}

.confirm-card {
    border-radius: 18px !important;
    padding: 28px 24px 20px;
    text-align: center;
}

.confirm-icon-wrap {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
}

.icon-bg--blue {
    background: #eff6ff;
}

.icon-bg--amber {
    background: #fffbeb;
}

.icon-bg--red {
    background: #fef2f2;
}

.confirm-title {
    font-size: 17px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 8px;
}

.confirm-body {
    font-size: 13px;
    color: #6b7280;
    line-height: 1.5;
    margin-bottom: 24px;
}

.confirm-actions {
    display: flex;
    gap: 10px;
}

.confirm-cancel-btn {
    flex: 1;
    border-radius: 30px !important;
    border-color: #d1d5db !important;
    color: #374151 !important;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
}

.confirm-ok-btn {
    flex: 1;
    border-radius: 30px !important;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
    box-shadow: none !important;
    color: #fff !important;
}

.btn--blue {
    background-color: #2563eb !important;
}

.btn--amber {
    background-color: #d97706 !important;
}

.btn--red {
    background-color: #ef4444 !important;
}
</style>